import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import classNames from 'classnames';
import { useSpring, animated } from '@react-spring/web';
import Portal from './portal';
import Mask from './mask';
import isNumber from 'lodash/isNumber';
import { Arrow } from './arrow';
import { withStopPropagation } from '@/utils/with-stop-propagation';
import { withNativeProps } from '@/utils/with-native-props';
import { arrow, autoUpdate, computePosition, flip, hide, limitShift, offset, shift, } from '@floating-ui/dom';
import { useInnerVisible } from '@/utils/use-inner-visible';
import { useLockScroll } from '@/utils/use-lock-scroll';
const Modal = (props, ref) => {
    const { prefix = 'cn-ui-m-', view = false, visible = false, container, disablePortal = false, bgImg = '', placement = 'center', className = '', maskClassName = '', containerClassName = '', containerStyle = {}, isFloatingUI = false, floating_mode = 'light', floating_content = '', floating_trigger, showMask = true, maskClosable = true, stopPropagation = [], usePortal = true, 
    // disableScroll = false,
    onMaskClick = () => { }, onClose = () => { }, onVisibleChange = () => { }, children, zIndex, style: styleProp = {}, ...others } = props;
    const [show, setShow] = useState(props.visible);
    const convertPx = (v) => v;
    // @ts-ignore
    useEffect(() => {
        if (show && !visible) {
            const t1 = setTimeout(() => setShow(false), 300);
            return () => clearTimeout(t1);
        }
        else {
            setShow(visible);
            onVisibleChange(visible);
        }
    }, [visible]);
    const handleMaskClick = (e) => {
        if (onMaskClick) {
            onMaskClick(e);
        }
        if (maskClosable && onClose) {
            onClose('mask', e);
            onVisibleChange(false, 'mask');
        }
    };
    const clsPrefix = `${prefix}modal`;
    const centerAnimationStyle = useSpring({
        scale: visible ? 1 : 0.8,
        opacity: visible ? 1 : 0,
        config: {
            mass: 1.2,
            tension: 200,
            friction: 25,
            clamp: true,
        },
    });
    const { percent } = useSpring({
        percent: visible ? 0 : 100,
        config: {
            precision: 0.1,
            mass: 0.4,
            tension: 300,
            friction: 30,
        },
    });
    const nonCenterAnimationStyle = {
        transform: percent.to((v) => {
            if (placement === 'bottom') {
                return `translate(0, ${v}%)`;
            }
            if (placement === 'top') {
                return `translate(0, -${v}%)`;
            }
            if (placement === 'left') {
                return `translate(-${v}%, 0)`;
            }
            if (placement === 'right') {
                return `translate(${v}%, 0)`;
            }
            return 'none';
        }),
    };
    const animationStyle = placement === 'center' ? centerAnimationStyle : nonCenterAnimationStyle;
    const containerRef = useRef(null);
    useLockScroll(containerRef, !!show);
    const modalContainer = (React.createElement(animated.div, { ...others, ref: containerRef, className: classNames(className, containerClassName, `${clsPrefix}-container`, `${clsPrefix}-container--${placement}`, {
            [`${clsPrefix}-container--${placement}-active`]: visible,
            [`${clsPrefix}-container--${placement}-inactive`]: !visible,
        }), style: {
            ...styleProp,
            ...containerStyle,
            background: bgImg ? `url(${bgImg})` : '',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            ...animationStyle,
        } }, children));
    const classPrefix = 'cn-ui-m-popover';
    const floatingRef = useRef(null);
    const arrowRef = useRef(null);
    const floatingElement = withStopPropagation(stopPropagation, withNativeProps(props, React.createElement("div", { className: classNames(classPrefix, `${classPrefix}-${floating_mode}`, !visible && `${classPrefix}-hidden`), ref: floatingRef },
        React.createElement("div", { className: `${classPrefix}-arrow`, ref: arrowRef },
            React.createElement(Arrow, { className: `${classPrefix}-arrow-icon` })),
        React.createElement("div", { className: `${classPrefix}-inner` },
            React.createElement("div", { className: `${classPrefix}-inner-content` }, floating_content)))));
    const [targetElement, setTargetElement] = useState(null);
    async function floating_update(targetRef) {
        const target = targetRef
            ? targetRef.current?.element ?? null
            : targetElement;
        const floating = floatingRef.current;
        const arrowElement = arrowRef.current;
        setTargetElement(target);
        if (!target || !floating || !arrowElement)
            return;
        const { x, y, placement: realPlacement, middlewareData, } = await computePosition(target, floating, {
            placement,
            middleware: [
                offset(convertPx(12)),
                shift({
                    padding: convertPx(4),
                    crossAxis: false,
                    limiter: limitShift(),
                }),
                flip(),
                hide(),
                arrow({
                    element: arrowElement,
                    padding: convertPx(12),
                }),
            ],
        });
        Object.assign(floating.style, {
            left: `${x}px`,
            top: `${y}px`,
        });
        const side = realPlacement.split('-')[0];
        const arrowSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
        }[side];
        const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {};
        Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            right: '',
            bottom: '',
            [arrowSide]: `-${convertPx(8)}px`,
        });
        const arrowRotate = {
            top: '0deg',
            bottom: '180deg',
            left: '270deg',
            right: '90deg',
        }[side];
        arrowElement.style.setProperty('--arrow-icon-rotate', arrowRotate);
    }
    useEffect(() => {
        const floatingEle = floatingRef.current;
        if (!targetElement || !floatingEle)
            return;
        return autoUpdate(targetElement, floatingEle, () => {
            setTargetElement(targetElement);
            floating_update();
        }, {
            elementResize: typeof ResizeObserver !== 'undefined',
        });
    }, [targetElement]);
    useImperativeHandle(ref, () => {
        const retRef = {
            targetElement,
            floating_update,
            container,
            floatingRef,
            floatingElement,
        };
        return retRef;
    }, [targetElement, floating_update, container, floatingElement]);
    const maskVisible = useInnerVisible(!!(show && visible));
    const element = show ? (React.createElement("div", { 
        // disable-scroll={disableScroll}
        // catchTouchMove={disableScroll}
        className: classNames(className, `${clsPrefix}`, `${clsPrefix}--${placement}`, {
            [`${clsPrefix}--no-portal`]: !usePortal,
        }), ref: ref, style: isNumber(zIndex) ? { zIndex } : null },
        showMask && !isFloatingUI ? (React.createElement(Mask, { ref: ref, className: maskClassName, visible: maskVisible, onClick: handleMaskClick })) : null,
        modalContainer)) : null;
    if (disablePortal || view) {
        return element;
    }
    // for theme config
    if (!usePortal) {
        return (React.createElement("div", { ref: ref, className: `${clsPrefix}-theme-wrapper`, style: { background: bgImg ? `url(${bgImg})` : '' } }, element));
    }
    return (React.createElement(Portal, { container: container }, isFloatingUI ? floatingElement : element));
};
Modal.displayName = 'CnModal';
const CnModal = forwardRef(Modal);
export { CnModal };
